import { demoModeAtom, tokenExpiresAtom, userAtom } from '@/global-store';
import { isPullRequestDashboardHostname } from '@/helpers/isPullRequestDashboardHostname';
import { createUnauthenticatedError } from '@/http/authErrors';
import { api, demoTokenExpires, demoUser } from '@/http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import type { UserData } from '@eye-able/types';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

const offlineMode = import.meta.env.VITE_OFFLINE_MODE === 'true';

interface SessionUserData extends UserData {
  tokenExpires: number;
}

export function useFetchWhoami() {
  const [user, setUser] = useAtom(userAtom);
  const [tokenExpires, setTokenExpires] = useAtom(tokenExpiresAtom);
  const [demoMode] = useAtom(demoModeAtom);

  return useQuery({
    queryKey: [api.whoami, tokenExpires, demoMode],
    initialData: user,
    queryFn: async () => {
      if (demoMode || offlineMode) {
        setTokenExpires(demoTokenExpires);
        setUser(demoUser);
        return demoUser;
      }

      const canBootstrapFromSharedCookie = isPullRequestDashboardHostname(
        window.location.hostname,
      );

      // Other environments keep treating local expiry state as authoritative.
      // PR previews first ask the API so a cookie raised by another preview can
      // initialise this origin's local state.
      if (!canBootstrapFromSharedCookie && !tokenExpires) {
        throw createUnauthenticatedError('No tokenExpires');
      }

      if (
        !canBootstrapFromSharedCookie &&
        Number(tokenExpires) <
          Number((new Date().getTime() / 1000).toFixed(0)) - 14400
      ) {
        throw createUnauthenticatedError('Token expired');
      }

      const data = await fetchWrapper.get<SessionUserData>(api.whoami);

      setUser(data);
      setTokenExpires(data.tokenExpires);

      return data;
    },
  });
}
